/**
 * Explore's handover token.
 *
 * A customer Explore sends here lands with `?ebx=<token>` on whichever booking URL it was. The
 * token is kept for the rest of the tab's visit - keyed by the business, since the flow moves
 * between routes (and an old-style landing is redirected) - and goes back to our server with
 * identification and scheduling, which pass it on to Explore. Nothing here shows or depends on it:
 * with no token, or no sessionStorage, the flow is exactly what it was.
 */

const PARAM = 'ebx';
const KEY_PREFIX = 'ebx:';
// Whatever the landing URL's first segment was - an old-style landing is a token, not a business,
// and is redirected to the business before any call is made.
const LANDING_KEY = `${KEY_PREFIX}*`;
const MAX_LENGTH = 1000;

const storage = () => {
    try {
        return window.sessionStorage;
    } catch (e) {
        return null;
    }
};

const firstSegment = (pathname) => {
    const segment = String(pathname || '').split('/').filter(Boolean)[0];
    return segment ? segment.toLowerCase() : null;
};

const safeSet = (key, value) => {
    try {
        const store = storage();
        store && store.setItem(key, value);
    } catch (e) {
        // Private mode, quota - analytics is never worth an error.
    }
};

const safeGet = (key) => {
    try {
        const store = storage();
        return store ? store.getItem(key) : null;
    } catch (e) {
        return null;
    }
};

/**
 * Keeps the token the landing URL brought, if any. Called once, before the app renders.
 *
 * @param {Location} [location]
 */
export const captureExploreToken = (location = window.location) => {
    let token;
    try {
        token = new URLSearchParams(location.search).get(PARAM);
    } catch (e) {
        return;
    }

    if (!token || token.length > MAX_LENGTH) {
        return;
    }

    const business = firstSegment(location.pathname);
    if (business) {
        safeSet(`${KEY_PREFIX}${business}`, token);
    }

    safeSet(LANDING_KEY, token);
};

/**
 * The token for the business being booked with, or undefined when Explore did not send them.
 *
 * @param {Location} [location]
 * @returns {string|undefined}
 */
export const exploreToken = (location = window.location) => {
    const business = firstSegment(location.pathname);
    return (business && safeGet(`${KEY_PREFIX}${business}`)) || safeGet(LANDING_KEY) || undefined;
};
